// =============================================================================
// VocAssessmentScreen — /hr/vocs/:assessment_id
// =============================================================================
// T-313 / T-252 Phase A. Renders the schema_json for a VOC template and
// captures the assessor's answers + dual signatures.
//
// Modes:
//   - Draft (no signatures yet) → editable form. Save draft button writes
//     answers_json + outcome + gaps/restrictions but doesn't lock.
//   - Partially signed (assessor only) → read-only answers, can apply
//     assessee signature.
//   - Fully signed → read-only everything. Director can review (Phase B has
//     the dedicated /admin/vocs/review queue; for now any director with the
//     link can review here).
//
// Schema_json shape comes from migration 160:
//   intro · prerequisites.items[] · theory.questions[] ·
//   practical.groups[].items[] · hazards.items[] · outcome.options[]
//
// Answers shape (stored in voc_assessments.answers_json):
//   prerequisites: { p1: 'sighted'|'na', ... }
//   theory:        { q1: 'competent'|'not_yet_competent', ... }
//   practical:     { a1: { result: 'competent'|'not_yet_competent', comment: '...' }, ... }
//   hazards:       { acknowledged: true }
// =============================================================================
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, ShieldCheck, AlertTriangle, CheckCircle2, ClipboardList, Trash2,
  BookOpen, Eye, Save, PenLine, Lock, FileDown,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Button, Card, Pill, Spinner, EmptyState, Field, Textarea } from "@/components/ui";
import { format, parseISO } from "date-fns";
import { SignaturePad, SignaturePadHandle } from "@/features/signature/SignaturePad";
import { downloadVocCertPdf } from "./vocCertPdf";

// ---- Matching a prerequisite to the ticket that satisfies it ----------------
// The picker lists every active licence a person holds, ordered by expiry. On a
// worker with fourteen tickets that made the RIGHT one hard to find and the
// wrong one easy to click - James's EWP form had "Working at Heights" pointing
// at a Tower Rescue card. Same class of error as the bug this whole change set
// exists to fix, arriving from the other direction.
//
// So: score each licence against the prerequisite's wording and float the likely
// ones to the top. Deliberately a SUGGESTION, not a restriction - an Advanced
// rigging ticket against a "Basic or higher" requirement is a correct answer
// that no simple rule would predict, and that is exactly the case that started
// all this.
const PREREQ_STOPWORDS = new Set([
  "current", "or", "and", "the", "a", "an", "of", "for", "to", "in", "on",
  "higher", "appropriate", "plant", "type", "class", "licence", "license",
  "card", "sighted", "recorded", "must", "be", "minimum", "e", "g", "eg",
  "where", "applicable", "s",
  // "operator" is the trap: it matched "HRWL or EWPA Operator Card" to
  // "VOC - Capstan Winch Operator", which is precisely the wrong-ticket error
  // this is meant to prevent. Too generic to carry meaning here.
  "operator", "worker", "site",
]);

function prereqTokens(text: string): string[] {
  return String(text ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .split(" ")
    .filter(w => w.length > 1 && !PREREQ_STOPWORDS.has(w));
}

// Shared meaningful words. Longer words count for more - "rigging" matching is
// worth more than "site" matching.
function prereqScore(itemLabel: string, licenceName: string): number {
  const a = prereqTokens(itemLabel);
  const b = prereqTokens(licenceName);
  // Exact word match scores full; a prefix match scores less. The prefix rule
  // exists for real abbreviations that differ by a letter - "EWPA" on the
  // requirement against "EWP" on the card - and is capped at 3+ characters so
  // it cannot fire on fragments.
  return a.reduce((n, w) => {
    if (b.includes(w)) return n + Math.min(w.length, 8);
    const near = b.some(x =>
      x.length >= 3 && w.length >= 3 && (x.startsWith(w) || w.startsWith(x)));
    return near ? n + 3 : n;
  }, 0);
}

// ---- Types -----------------------------------------------------------------

type CompResult = "competent" | "not_yet_competent";
type Sighted = "sighted" | "na";

interface VocSchemaJson {
  intro?:         { purpose?: string; scope?: string };
  prerequisites?: { instruction?: string; items: { id: string; label: string; note?: string }[] };
  theory?:        { instruction?: string; questions: { id: string; number?: number; question: string; model_answer?: string }[] };
  practical?:     { instruction?: string; groups: { id: string; title: string; items: { id: string; competency: string }[] }[] };
  hazards?:       { instruction?: string; items: { id: string; hazard: string; consequence?: string; control?: string }[] };
  outcome?:       { options: { value: string; label: string }[] };
}

interface Assessment {
  voc_assessment_id:   string;
  voc_template_id:     string;
  template_code:       string;
  template_version:    string;
  employee_id:         string;
  assessor_user_id:    string;
  assessed_at:         string;
  answers_json:        any;
  outcome:             string;
  gaps_notes:          string | null;
  restrictions:        string | null;
  assessor_signature_svg:  string | null;
  assessor_signed_at:      string | null;
  assessee_signature_svg:  string | null;
  assessee_signed_at:      string | null;
  director_review_user_id: string | null;
  director_reviewed_at:    string | null;
  employee_licence_id:     string | null;
}

interface Template {
  voc_template_id: string;
  code:            string;
  title:           string;
  asset_kind:      string;
  asset_subtype:   string | null;
  version:         string;
  validity_years:  number;
  schema_json:     VocSchemaJson;
}

interface Employee {
  employee_id: string;
  first_name:  string | null;
  last_name:   string | null;
  primary_role_name: string | null;
}

// ---- Component -------------------------------------------------------------

export default function VocAssessmentScreen() {
  const user = useRequireAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { assessment_id } = useParams<{ assessment_id: string }>();
  const canManage = hasRole(user, "manager");

  // T-313 / mig 161: voc_* tables moved from app schema to public.
  // RPC calls stay on .schema("app") since that's where the functions live.
  const { data: assessment, isLoading: loadingA } = useQuery({
    queryKey: ["voc_assessment", assessment_id],
    enabled: !!assessment_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("voc_assessments")
        .select("*")
        .eq("voc_assessment_id", assessment_id!)
        .single();
      if (error) throw error;
      return data as Assessment;
    },
  });

  const { data: template } = useQuery({
    queryKey: ["voc_template_for_assessment", assessment?.voc_template_id],
    enabled: !!assessment?.voc_template_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("voc_templates")
        .select("voc_template_id, code, title, asset_kind, asset_subtype, version, validity_years, schema_json")
        .eq("voc_template_id", assessment!.voc_template_id)
        .single();
      if (error) throw error;
      return data as Template;
    },
  });

  const { data: employee } = useQuery({
    queryKey: ["voc_employee_for_assessment", assessment?.employee_id],
    enabled: !!assessment?.employee_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employees")
        .select("employee_id, first_name, last_name, primary_role_name")
        .eq("employee_id", assessment!.employee_id)
        .single();
      if (error) throw error;
      return data as Employee;
    },
  });

  // ---------------------------------------------------------------------------
  // The person's actual tickets, and what has been recorded against each
  // prerequisite. Both needed so the certificate can name the ticket that was
  // really sighted instead of the template's generic label.
  // ---------------------------------------------------------------------------
  const { data: empLicences = [] } = useQuery({
    queryKey: ["voc_emp_licences", assessment?.employee_id],
    enabled: !!assessment?.employee_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employee_licences")
        .select("licence_id, licence_number, expiry_date, issue_date, state_issued, is_active, verified_at, licence_types(code, display_name, category)")
        .eq("employee_id", assessment!.employee_id)
        .eq("is_active", true)
        .order("expiry_date", { ascending: false, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  const { data: prereqEvidence = [], refetch: refetchEvidence } = useQuery({
    queryKey: ["voc_prereq_evidence", assessment_id],
    enabled: !!assessment_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("voc_prerequisite_evidence")
        .select("evidence_id, prereq_item_id, employee_licence_id, outcome, note, recorded_at")
        .eq("voc_assessment_id", assessment_id!);
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  const evidenceByItem = useMemo(() => {
    const m = new Map<string, any>();
    (prereqEvidence ?? []).forEach(e => m.set(e.prereq_item_id, e));
    return m;
  }, [prereqEvidence]);

  const licenceById = useMemo(() => {
    const m = new Map<string, any>();
    (empLicences ?? []).forEach(l => m.set(l.licence_id, l));
    return m;
  }, [empLicences]);

  // Recording which ticket satisfied a prerequisite is evidence ABOUT the
  // assessment, not part of the signed finding - so it is allowed on a locked
  // assessment. That is deliberate: Jairyn's Nullarbor VOC is signed and still
  // needs its rigging ticket named correctly. Nothing here can touch the
  // outcome, the dates or the signatures.
  const [err, setErr] = useState<string | null>(null);
  const [evidenceBusy, setEvidenceBusy] = useState<string | null>(null);

  async function recordEvidence(itemId: string, licenceId: string | null) {
    setEvidenceBusy(itemId);
    const { error } = await supabase
      .from("voc_prerequisite_evidence")
      .upsert({
        voc_assessment_id:   assessment_id!,
        prereq_item_id:      itemId,
        employee_licence_id: licenceId,
        outcome:             "sighted",
        recorded_by_user_id: (user as any)?.user_id ?? null,
        recorded_at:         new Date().toISOString(),
      }, { onConflict: "voc_assessment_id,prereq_item_id" });
    setEvidenceBusy(null);
    if (error) { setErr(`Couldn't record the ticket: ${error.message}`); return; }
    refetchEvidence();
  }

  // Local form state (initialised from assessment row when loaded)
  const [answers, setAnswers] = useState<any>({});
  const [outcome, setOutcome] = useState<string>("draft");
  const [gapsNotes, setGapsNotes] = useState("");
  const [restrictions, setRestrictions] = useState("");
  const [showAssessorSign, setShowAssessorSign] = useState(false);
  const [showAssesseeSign, setShowAssesseeSign] = useState(false);

  useEffect(() => {
    if (assessment) {
      setAnswers(assessment.answers_json ?? {});
      setOutcome(assessment.outcome ?? "draft");
      setGapsNotes(assessment.gaps_notes ?? "");
      setRestrictions(assessment.restrictions ?? "");
    }
  }, [assessment?.voc_assessment_id]);

  // Lock state
  const isLocked = !!(assessment?.assessor_signed_at && assessment?.assessee_signed_at);
  const hasAssessorSig = !!assessment?.assessor_signed_at;
  const hasAssesseeSig = !!assessment?.assessee_signed_at;
  const isDirector = hasRole(user, "director");
  const needsReview = isLocked && !assessment?.director_reviewed_at;

  // ---- Mutations ---------------------------------------------------------

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!assessment_id) throw new Error("No assessment id");
      const { error } = await supabase
        .from("voc_assessments")
        .update({
          answers_json: answers,
          outcome:      outcome,
          gaps_notes:   gapsNotes || null,
          restrictions: restrictions || null,
        })
        .eq("voc_assessment_id", assessment_id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["voc_assessment", assessment_id] }),
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : "Save failed"),
  });

  const signMut = useMutation({
    mutationFn: async (args: { who: "assessor" | "assessee"; svg: string }) => {
      // Save the latest form state first if assessor is signing (assessee can't change anything)
      if (args.who === "assessor") {
        await saveMut.mutateAsync();
      }
      const { error } = await supabase
        .schema("app")
        .rpc("sign_voc_assessment", {
          p_voc_assessment_id: assessment_id!,
          p_who:               args.who,
          p_signature_svg:     args.svg,
          p_method:            "canvas",
        });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["voc_assessment", assessment_id] });
      setShowAssessorSign(false);
      setShowAssesseeSign(false);
    },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : "Signature failed"),
  });

  const reviewMut = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .schema("app")
        .rpc("review_voc_assessment", {
          p_voc_assessment_id: assessment_id!,
          p_review_notes:      null,
        });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["voc_assessment", assessment_id] }),
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : "Review failed"),
  });

  // ---------------------------------------------------------------------------
  // Discard a draft.
  //
  // There was no way to remove an abandoned assessment at all, so a VOC started
  // by mistake sat on the person's record for good - Jairyn Buckney had two
  // Capstan Winch VOCs, one real and one a stray draft, and anyone looking had
  // to work out which counted.
  //
  // Only ever a DRAFT. app.tg_voc_assessments_no_delete_signed() raises on any
  // attempt to delete a signed assessment, so the database is the real guard
  // here and this button cannot do worse than fail.
  // ---------------------------------------------------------------------------
  const discardMut = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("voc_assessments")
        .delete()
        .eq("voc_assessment_id", assessment_id!);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["employee_vocs"] });
      navigate(-1);
    },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : "Couldn't discard this draft"),
  });

  if (!user) return null;
  if (loadingA || !assessment || !template) {
    return <div className="p-10 flex justify-center"><Spinner size={20} /></div>;
  }
  if (!canManage && assessment.employee_id !== user.employee_id) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <EmptyState
            title="Access denied"
            description="You can only view your own VOC assessments. Manager+ to view other employees."
            icon={Lock}
          />
        </Card>
      </div>
    );
  }

  const schema = template.schema_json ?? {};
  const empName = employee
    ? [employee.first_name, employee.last_name].filter(Boolean).join(" ")
    : assessment.employee_id;
  const editable = !isLocked && canManage;

  function setSectionAnswer(section: string, key: string, value: any) {
    setAnswers((prev: any) => ({
      ...prev,
      [section]: { ...(prev?.[section] ?? {}), [key]: value },
    }));
  }
  function setPracticalAnswer(itemId: string, patch: { result?: CompResult; comment?: string }) {
    setAnswers((prev: any) => {
      const next = { ...(prev ?? {}) };
      const sec = { ...(next.practical ?? {}) };
      sec[itemId] = { ...(sec[itemId] ?? {}), ...patch };
      next.practical = sec;
      return next;
    });
  }

  // ---- Render --------------------------------------------------------------

  return (
    <div className="p-6 max-w-4xl mx-auto pb-24">
      <Link
        to={`/hr/vocs/employee/${assessment.employee_id}`}
        className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3"
      >
        <ArrowLeft size={14} /> Back to {empName}'s VOCs
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div>
          <div className="text-text-muted text-[11px] uppercase tracking-wider2 font-mono mb-1">
            {template.code} · v{template.version}
          </div>
          <h1 className="text-[22px] font-semibold tracking-tight flex items-center gap-2">
            <ShieldCheck size={20} className="text-accent" /> {template.title}
          </h1>
          <div className="text-[13px] text-text-muted mt-1 font-mono">
            Assessee: {empName} · Assessed: {format(parseISO(assessment.assessed_at), "dd MMM yyyy")}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isLocked ? <Pill label="signed + locked" tone="approved" small /> : <Pill label="draft" tone="pending" small />}
          {needsReview && isDirector && (
            <Button size="sm" icon={CheckCircle2} onClick={() => reviewMut.mutate()} disabled={reviewMut.isPending}>
              Director review
            </Button>
          )}
          {assessment.director_reviewed_at && <Pill label="director reviewed" tone="approved" small />}
          {/* Drafts only. A signed assessment is protected by the database. */}
          {!isLocked && canManage && (
            <Button
              size="sm" variant="danger" icon={Trash2}
              disabled={discardMut.isPending}
              onClick={() => {
                if (!window.confirm(
                  "Discard this draft assessment?\n\nIt has not been signed, so nothing is lost from the record. This cannot be undone."
                )) return;
                discardMut.mutate();
              }}
            >
              {discardMut.isPending ? "Discarding…" : "Discard draft"}
            </Button>
          )}
          {/* T-314: cert PDF export. Only render when assessment is locked
              AND outcome is competent. Director (or manager+) can grab the
              client-facing Letter of Competency to send to Ventia / clients. */}
          {isLocked && assessment.outcome === "competent" && canManage && (
            <Button
              size="sm"
              icon={FileDown}
              onClick={() => {
                // The prerequisite line names the tickets ACTUALLY SIGHTED for this
                // person, not the template's generic labels. Before this, every
                // certificate off a template repeated that template's wording -
                // which is how Jairyn's letter came to say "Rigging - Basic" when
                // he holds Advanced. Falls back to the template label only where
                // no ticket has been recorded, and no longer truncates at three.
                const prereqs = (schema.prerequisites?.items ?? []).map(p => {
                  const ev  = evidenceByItem.get(p.id);
                  const lic = ev?.employee_licence_id ? licenceById.get(ev.employee_licence_id) : null;
                  if (!lic) return p.label;
                  const name = lic.licence_types?.display_name ?? p.label;
                  const num  = lic.licence_number ? ` (${lic.licence_number})` : "";
                  const exp  = lic.expiry_date
                    ? `, expires ${format(parseISO(lic.expiry_date), "dd MMM yyyy")}`
                    : "";
                  return `${name}${num}${exp}`;
                }).join("; ");
                downloadVocCertPdf({
                  voc_assessment_id:    assessment.voc_assessment_id,
                  template_code:        assessment.template_code,
                  template_title:       template.title,
                  asset_kind:           template.asset_kind,
                  validity_years:       template.validity_years,
                  assessed_at:          assessment.assessed_at,
                  outcome:              assessment.outcome,
                  employee_full_name:   empName,
                  employee_role:        employee?.primary_role_name?.replace(/_/g, " ") ?? "Field Crew",
                  intro_purpose:        schema.intro?.purpose ?? null,
                  prerequisite_summary: prereqs || null,
                  director_name:        "James O'Sullivan",
                  director_email:       "hse@coretelgroup.com",
                  director_phone:       "0423 661 366",
                  // T-314 hotfix: pull the assessor SVG signature into the
                  // cert. Works while James is both the Director AND the
                  // assessor (current Coretel scale). Phase C will switch
                  // to a per-Director on-file signature lookup.
                  director_signature_svg: assessment.assessor_signature_svg,
                  // A certificate is a REISSUE when prerequisite evidence was
                  // recorded after both signatures were on the assessment - i.e.
                  // a copy may already have gone to a client with the template's
                  // generic wording. Say so on the document rather than quietly
                  // handing over a different second copy.
                  reissue_note: (() => {
                    const signedAt = assessment.assessee_signed_at ?? assessment.assessor_signed_at;
                    if (!signedAt) return null;
                    const later = (prereqEvidence ?? [])
                      .map((e: any) => e.recorded_at)
                      .filter(Boolean)
                      .filter((t: string) => new Date(t) > new Date(signedAt));
                    if (!later.length) return null;
                    const when = later.sort()[later.length - 1];
                    return `Reissued ${format(parseISO(when), "d MMM yyyy")} — prerequisite ticket detail corrected to the qualification held and sighted at assessment. Assessment, outcome, date and signatures unchanged.`;
                  })(),
                }).catch(e => setErr(e instanceof Error ? e.message : "Cert PDF failed"));
              }}
            >
              Export Certificate
            </Button>
          )}
        </div>
      </div>

      {err && (
        <div className="mb-4 px-3 py-2 bg-danger/10 border border-danger/30 rounded text-[13px] text-danger flex items-center gap-2">
          <AlertTriangle size={13} /> {err}
        </div>
      )}

      {/* Intro / Purpose / Scope */}
      {schema.intro && (
        <Card className="mb-4">
          <div className="p-5">
            <div className="text-[11px] uppercase tracking-wider2 text-accent font-mono mb-2">Purpose</div>
            <p className="text-[13px] text-text leading-relaxed">{schema.intro.purpose}</p>
            {schema.intro.scope && (
              <>
                <div className="text-[11px] uppercase tracking-wider2 text-accent font-mono mt-3 mb-2">Scope</div>
                <p className="text-[13px] text-text leading-relaxed">{schema.intro.scope}</p>
              </>
            )}
          </div>
        </Card>
      )}

      {/* Prerequisites */}
      {schema.prerequisites && schema.prerequisites.items.length > 0 && (
        <Card className="mb-4">
          <div className="p-5">
            <div className="flex items-center gap-2 mb-1">
              <ClipboardList size={14} className="text-accent" />
              <h2 className="text-[15px] font-semibold text-text">Prerequisites</h2>
            </div>
            {schema.prerequisites.instruction && (
              <p className="text-[12px] text-text-muted mb-3 italic">{schema.prerequisites.instruction}</p>
            )}
            <div className="space-y-2">
              {schema.prerequisites.items.map(item => {
                const cur: Sighted | undefined = answers?.prerequisites?.[item.id];
                const ev  = evidenceByItem.get(item.id);
                const lic = ev?.employee_licence_id ? licenceById.get(ev.employee_licence_id) : null;
                // Show the picker once the item is marked sighted, or whenever
                // something has already been recorded against it - including on a
                // locked assessment, which is how an existing certificate gets
                // corrected without touching the signed record.
                const showPicker = canManage && (cur === "sighted" || !!ev);
                return (
                  <div key={item.id} className="p-3 bg-ink-raised border border-line rounded-md">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] text-text">{item.label}</div>
                        {item.note && <div className="text-[11px] text-text-muted mt-1 italic">{item.note}</div>}
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        {(["sighted", "na"] as Sighted[]).map(v => (
                          <button
                            key={v}
                            type="button"
                            disabled={!editable}
                            onClick={() => setSectionAnswer("prerequisites", item.id, v)}
                            className={`px-2 py-1 rounded text-[11px] border transition-colors ${
                              cur === v ? "bg-accent/15 border-accent text-accent" : "bg-ink-card border-line text-text-muted"
                            } ${editable ? "hover:border-line-light" : "opacity-70 cursor-not-allowed"}`}
                          >
                            {v === "sighted" ? "✓ Sighted" : "N/A"}
                          </button>
                        ))}
                      </div>
                    </div>

                    {showPicker && (
                      <div className="mt-2.5 pt-2.5 border-t border-line">
                        <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">
                          Which of their tickets was sighted?
                        </div>
                        {(() => {
                          const optLabel = (l: any) =>
                            `${l.licence_types?.display_name ?? "Licence"}`
                            + (l.licence_number ? ` · ${l.licence_number}` : "")
                            + (l.expiry_date ? ` · exp ${l.expiry_date}` : "");
                          const scored = (empLicences as any[]).map(l => ({
                            l, score: prereqScore(item.label, l.licence_types?.display_name ?? ""),
                          }));
                          const likely = scored.filter(x => x.score > 0)
                                               .sort((a, b) => b.score - a.score);
                          const rest   = scored.filter(x => x.score === 0);
                          return (
                            <select
                              value={ev?.employee_licence_id ?? ""}
                              disabled={evidenceBusy === item.id}
                              onChange={e => recordEvidence(item.id, e.target.value || null)}
                              className="w-full bg-ink border border-line rounded px-2 py-1.5 text-[12px] text-text"
                            >
                              <option value="">— not recorded —</option>
                              {likely.length > 0 && (
                                <optgroup label="Likely matches">
                                  {likely.map(({ l }) => (
                                    <option key={l.licence_id} value={l.licence_id}>{optLabel(l)}</option>
                                  ))}
                                </optgroup>
                              )}
                              {rest.length > 0 && (
                                <optgroup label={likely.length > 0 ? "All other tickets" : "All tickets"}>
                                  {rest.map(({ l }) => (
                                    <option key={l.licence_id} value={l.licence_id}>{optLabel(l)}</option>
                                  ))}
                                </optgroup>
                              )}
                            </select>
                          );
                        })()}
                        {lic ? (
                          <div className="text-[11px] text-ok mt-1">
                            The certificate will name this ticket, not the wording above.
                          </div>
                        ) : (
                          <div className="text-[11px] text-warn mt-1">
                            Nothing recorded — the certificate falls back to the template's generic wording.
                          </div>
                        )}
                        {isLocked && (
                          <div className="text-[11px] text-text-dim mt-1">
                            This records which ticket was sighted. It does not change the signed
                            assessment, its outcome or its signatures.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      )}

      {/* Theory */}
      {schema.theory && schema.theory.questions.length > 0 && (
        <Card className="mb-4">
          <div className="p-5">
            <div className="flex items-center gap-2 mb-1">
              <BookOpen size={14} className="text-accent" />
              <h2 className="text-[15px] font-semibold text-text">Theory / Knowledge Assessment</h2>
            </div>
            {schema.theory.instruction && (
              <p className="text-[12px] text-text-muted mb-3 italic">{schema.theory.instruction}</p>
            )}
            <div className="space-y-3">
              {schema.theory.questions.map(q => {
                const cur: CompResult | undefined = answers?.theory?.[q.id];
                return (
                  <details key={q.id} className="bg-ink-raised border border-line rounded-md group">
                    <summary className="p-3 cursor-pointer flex items-start gap-3">
                      <div className="text-[11px] font-mono text-text-muted mt-0.5 flex-shrink-0">Q{q.number ?? ""}</div>
                      <div className="flex-1 text-[13px] text-text">{q.question}</div>
                      <div className="flex gap-1 flex-shrink-0">
                        {(["competent", "not_yet_competent"] as CompResult[]).map(v => (
                          <button
                            key={v}
                            type="button"
                            disabled={!editable}
                            onClick={e => { e.preventDefault(); setSectionAnswer("theory", q.id, v); }}
                            className={`px-2 py-1 rounded text-[10px] uppercase tracking-wider2 font-mono border transition-colors ${
                              cur === v
                                ? (v === "competent" ? "bg-ok/15 border-ok text-ok" : "bg-danger/15 border-danger text-danger")
                                : "bg-ink-card border-line text-text-muted"
                            } ${editable ? "" : "opacity-70 cursor-not-allowed"}`}
                          >
                            {v === "competent" ? "C" : "NYC"}
                          </button>
                        ))}
                      </div>
                    </summary>
                    {q.model_answer && (
                      <div className="px-3 pb-3 pt-1 border-t border-line/40 mt-2">
                        <div className="text-[10px] uppercase tracking-wider2 text-text-dim font-mono mb-1">Model answer / notes</div>
                        <p className="text-[12px] text-text-muted leading-relaxed italic">{q.model_answer}</p>
                      </div>
                    )}
                  </details>
                );
              })}
            </div>
          </div>
        </Card>
      )}

      {/* Practical */}
      {schema.practical && schema.practical.groups.length > 0 && (
        <Card className="mb-4">
          <div className="p-5">
            <div className="flex items-center gap-2 mb-1">
              <Eye size={14} className="text-accent" />
              <h2 className="text-[15px] font-semibold text-text">Practical Observation Checklist</h2>
            </div>
            {schema.practical.instruction && (
              <p className="text-[12px] text-text-muted mb-3 italic">{schema.practical.instruction}</p>
            )}
            <div className="space-y-5">
              {schema.practical.groups.map(g => (
                <div key={g.id}>
                  <div className="text-[12px] uppercase tracking-wider2 text-accent font-mono mb-2">{g.title}</div>
                  <div className="space-y-2">
                    {g.items.map(it => {
                      const cur = answers?.practical?.[it.id] ?? {};
                      return (
                        <div key={it.id} className="bg-ink-raised border border-line rounded-md p-3">
                          <div className="flex items-start gap-3">
                            <div className="flex-1 text-[13px] text-text">{it.competency}</div>
                            <div className="flex gap-1 flex-shrink-0">
                              {(["competent", "not_yet_competent"] as CompResult[]).map(v => (
                                <button
                                  key={v}
                                  type="button"
                                  disabled={!editable}
                                  onClick={() => setPracticalAnswer(it.id, { result: v })}
                                  className={`px-2 py-1 rounded text-[10px] uppercase tracking-wider2 font-mono border transition-colors ${
                                    cur.result === v
                                      ? (v === "competent" ? "bg-ok/15 border-ok text-ok" : "bg-danger/15 border-danger text-danger")
                                      : "bg-ink-card border-line text-text-muted"
                                  } ${editable ? "" : "opacity-70 cursor-not-allowed"}`}
                                >
                                  {v === "competent" ? "C" : "NYC"}
                                </button>
                              ))}
                            </div>
                          </div>
                          {(editable || cur.comment) && (
                            <input
                              type="text"
                              disabled={!editable}
                              value={cur.comment ?? ""}
                              onChange={e => setPracticalAnswer(it.id, { comment: e.target.value })}
                              placeholder="Assessor comment (optional)"
                              className="mt-2 w-full px-2 py-1.5 rounded border border-line bg-ink-card text-text text-[12px]"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Hazards */}
      {schema.hazards && schema.hazards.items.length > 0 && (
        <Card className="mb-4">
          <div className="p-5">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle size={14} className="text-warn" />
              <h2 className="text-[15px] font-semibold text-text">Hazard & Risk Awareness</h2>
            </div>
            {schema.hazards.instruction && (
              <p className="text-[12px] text-text-muted mb-3 italic">{schema.hazards.instruction}</p>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono border-b border-line">
                    <th className="text-left py-2 pr-3">Hazard</th>
                    <th className="text-left py-2 pr-3">Consequence</th>
                    <th className="text-left py-2 pl-3">Control</th>
                  </tr>
                </thead>
                <tbody>
                  {schema.hazards.items.map(h => (
                    <tr key={h.id} className="border-b border-line/40">
                      <td className="py-2 pr-3 text-text font-medium align-top">{h.hazard}</td>
                      <td className="py-2 pr-3 text-text-muted align-top">{h.consequence}</td>
                      <td className="py-2 pl-3 text-text-muted align-top">{h.control}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <label className="mt-3 flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                disabled={!editable}
                checked={!!answers?.hazards?.acknowledged}
                onChange={e => setSectionAnswer("hazards", "acknowledged", e.target.checked)}
                className="h-4 w-4"
              />
              <span className="text-[12px] text-text">Assessee confirms awareness of all hazards + controls listed above.</span>
            </label>
          </div>
        </Card>
      )}

      {/* Outcome */}
      {schema.outcome && (
        <Card className="mb-4">
          <div className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 size={14} className="text-accent" />
              <h2 className="text-[15px] font-semibold text-text">Assessment Outcome</h2>
            </div>
            <div className="space-y-2 mb-4">
              {schema.outcome.options.map(opt => (
                <label key={opt.value} className={`block p-3 rounded-md border-2 cursor-pointer transition-colors ${
                  outcome === opt.value
                    ? (opt.value === "competent" ? "bg-ok/10 border-ok" : opt.value === "not_yet_competent" ? "bg-danger/10 border-danger" : "bg-warn/10 border-warn")
                    : "bg-ink-raised border-line hover:border-line-light"
                } ${!editable ? "cursor-not-allowed opacity-70" : ""}`}>
                  <input
                    type="radio"
                    name="outcome"
                    value={opt.value}
                    checked={outcome === opt.value}
                    onChange={() => editable && setOutcome(opt.value)}
                    disabled={!editable}
                    className="mr-2"
                  />
                  <span className="text-[13px] text-text font-medium">{opt.label}</span>
                </label>
              ))}
            </div>
            <Field label="Gaps / training plan (if applicable)">
              <Textarea rows={2} value={gapsNotes} onChange={e => setGapsNotes(e.target.value)} disabled={!editable} />
            </Field>
            <div className="mt-3">
              <Field label="Restrictions / conditions on authorisation">
                <Textarea rows={2} value={restrictions} onChange={e => setRestrictions(e.target.value)} disabled={!editable} />
              </Field>
            </div>
          </div>
        </Card>
      )}

      {/* Signatures */}
      <Card className="mb-4">
        <div className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <PenLine size={14} className="text-accent" />
            <h2 className="text-[15px] font-semibold text-text">Sign-offs</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Assessor */}
            <div className="bg-ink-raised border border-line rounded-md p-4">
              <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono mb-2">Assessor</div>
              {hasAssessorSig ? (
                <>
                  <div className="bg-ink-card border border-line-light/40 rounded p-2 mb-2 max-h-[120px] overflow-hidden" dangerouslySetInnerHTML={{ __html: assessment.assessor_signature_svg ?? "" }} />
                  <div className="text-[11px] text-text-muted font-mono">
                    Signed {format(parseISO(assessment.assessor_signed_at!), "dd MMM yyyy HH:mm")}
                  </div>
                </>
              ) : editable ? (
                <Button variant="secondary" size="sm" icon={PenLine} onClick={() => setShowAssessorSign(true)}>
                  Sign as assessor
                </Button>
              ) : (
                <div className="text-[11px] text-text-dim italic">Not yet signed.</div>
              )}
            </div>

            {/* Assessee */}
            <div className="bg-ink-raised border border-line rounded-md p-4">
              <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono mb-2">Assessee</div>
              {hasAssesseeSig ? (
                <>
                  <div className="bg-ink-card border border-line-light/40 rounded p-2 mb-2 max-h-[120px] overflow-hidden" dangerouslySetInnerHTML={{ __html: assessment.assessee_signature_svg ?? "" }} />
                  <div className="text-[11px] text-text-muted font-mono">
                    Signed {format(parseISO(assessment.assessee_signed_at!), "dd MMM yyyy HH:mm")}
                  </div>
                </>
              ) : hasAssessorSig && canManage ? (
                <Button variant="secondary" size="sm" icon={PenLine} onClick={() => setShowAssesseeSign(true)}>
                  Hand to assessee to sign
                </Button>
              ) : (
                <div className="text-[11px] text-text-dim italic">
                  {hasAssessorSig ? "Awaiting assessee." : "Assessor signs first."}
                </div>
              )}
            </div>
          </div>
          {isLocked && (
            <div className="mt-3 px-3 py-2 bg-ok/10 border border-ok/30 rounded text-[12px] text-ok flex items-center gap-2">
              <Lock size={12} />
              Both signatures recorded. Assessment is locked + immutable. Auto-licence created on the employee's record.
            </div>
          )}
        </div>
      </Card>

      {/* Save bar (when editable) */}
      {editable && (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="secondary"
            icon={Save}
            onClick={() => { setErr(null); saveMut.mutate(); }}
            disabled={saveMut.isPending}
          >
            {saveMut.isPending ? "Saving…" : "Save draft"}
          </Button>
        </div>
      )}

      {/* Signature pad modals */}
      {showAssessorSign && (
        <SignatureModal
          title="Assessor signature"
          onCancel={() => setShowAssessorSign(false)}
          onConfirm={(svg) => signMut.mutate({ who: "assessor", svg })}
          isPending={signMut.isPending}
        />
      )}
      {showAssesseeSign && (
        <SignatureModal
          title="Assessee signature"
          subtitle={`Hand the device to ${empName} to confirm participation + acceptance.`}
          onCancel={() => setShowAssesseeSign(false)}
          onConfirm={(svg) => signMut.mutate({ who: "assessee", svg })}
          isPending={signMut.isPending}
        />
      )}
    </div>
  );
}

// ---- SignatureModal — tight wrapper around SignaturePad --------------------

function SignatureModal({
  title, subtitle, onCancel, onConfirm, isPending,
}: { title: string; subtitle?: string; onCancel: () => void; onConfirm: (svg: string) => void; isPending: boolean }) {
  const padRef = useRef<SignaturePadHandle | null>(null);
  const [hasStroke, setHasStroke] = useState(false);
  function confirm() {
    const svg = padRef.current?.getSvg();
    if (svg) onConfirm(svg);
  }
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center pt-12 px-4"
      onClick={isPending ? undefined : onCancel}
    >
      <div className="bg-ink-card border border-line rounded-lg shadow-xl w-full max-w-2xl p-5" onClick={e => e.stopPropagation()}>
        <h2 className="text-[16px] font-semibold text-text">{title}</h2>
        {subtitle && <p className="text-[12px] text-text-muted mt-1">{subtitle}</p>}
        <div className="mt-4">
          <SignaturePad ref={padRef} onFirstStroke={() => setHasStroke(true)} />
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => { padRef.current?.clear(); setHasStroke(false); }} disabled={isPending}>Clear</Button>
          <Button variant="secondary" onClick={onCancel} disabled={isPending}>Cancel</Button>
          <Button
            icon={CheckCircle2}
            onClick={confirm}
            disabled={!hasStroke || isPending}
          >
            {isPending ? "Recording…" : "Confirm signature"}
          </Button>
        </div>
      </div>
    </div>
  );
}
